import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, CalendarCheck, Clock, ChevronDown, Search } from 'lucide-react';
import { api } from '../../lib/api';

const STATUS_STYLE = {
  green: { dot: 'bg-[#15803d]', chip: 'bg-[#dcfce7] text-[#166534]' },
  orange: { dot: 'bg-[#d97706]', chip: 'bg-[#fef3c7] text-[#92400e]' },
  red: { dot: 'bg-[#b91c1c]', chip: 'bg-[#fee2e2] text-[#991b1b]' },
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : null;
const fmtT = (iso) => new Date(iso).toLocaleTimeString('es-GT', { hour: 'numeric', minute: '2-digit' });

// MO-P07 para Territory Managers: cumplimiento de trackings diarios por estación.
const TrackingCompliance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState('');
  const [fStatus, setFStatus] = useState('all');

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const r = await api.get('/tracking-compliance');
      setData(r.data);
    } catch { /* noop */ }
    finally { if (initial) setLoading(false); }
  }, []);

  useEffect(() => { load(true); const t = setInterval(() => load(false), 60 * 1000); return () => clearInterval(t); }, [load]);

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;
  }
  const stations = (data?.stations || []).filter(s => {
    if (fStatus !== 'all' && s.status !== fStatus) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [s.name, s.brand, s.bu, s.region].some(v => (v || '').toLowerCase().includes(t));
  });
  const target = data?.target || 3;

  return (
    <div className="px-5 py-5" data-testid="tm-tracking-compliance">
      <h2 className="font-serif text-[28px] leading-tight tracking-tight">Trackings de hoy</h2>
      <p className="text-[13px] text-[#5a5a5a] mb-1">
        {new Date().toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
      <p className="text-[12.5px] mb-5" data-testid="tm-compliance-summary">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${data?.complete === data?.total && data?.total > 0 ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#f3f0e9] text-[#3a3a3a]'}`}>
          <CalendarCheck size={12}/> {data?.complete ?? 0} de {data?.total ?? 0} estaciones completas · meta según horario de cada estación
        </span>
      </p>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]" />
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar estación…"
          className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#e8e5de] bg-white text-[13px] outline-none focus:border-[#0f1d6f]"
          data-testid="compliance-search-input"
        />
      </div>

      <div className="flex gap-1.5 mb-4 overflow-x-auto no-scrollbar -mx-5 px-5">
        {[['all', 'Todas'], ['green', 'Completas'], ['orange', 'Parciales'], ['red', 'Sin tracking']].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFStatus(k)}
            className={`shrink-0 text-[12px] px-3.5 py-1.5 rounded-full border transition-colors ${fStatus === k ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#5a5a5a] border-[#e8e5de]'}`}
            data-testid={`compliance-filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {stations.length === 0 ? (
        <p className="text-[13px] text-[#8a8579] py-10 text-center">
          {q.trim() || fStatus !== 'all' ? 'Sin estaciones que coincidan con el filtro.' : 'No tienes estaciones asignadas.'}
        </p>
      ) : (
        <div className="space-y-2.5">
          {stations.map(s => {
            const st = STATUS_STYLE[s.status] || STATUS_STYLE.red;
            const open = openId === s.id;
            return (
              <div key={s.id} className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden" data-testid={`compliance-card-${s.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : s.id)}
                  className="w-full p-4 flex items-center gap-3 text-left"
                  data-testid={`compliance-toggle-${s.id}`}
                >
                  <span className={`w-3 h-3 rounded-full shrink-0 ${st.dot}`} data-testid={`semaforo-${s.id}`}/>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-medium leading-tight truncate">{s.name}</p>
                    <p className="text-[10.5px] text-[#8a8579]">{s.brand}{s.bu ? ` · BU ${s.bu}` : ''}{s.region ? ` · ${s.region}` : ''}</p>
                  </div>
                  <span className={`text-[11.5px] font-mono px-2 py-0.5 rounded-full shrink-0 ${st.chip}`} data-testid={`compliance-count-${s.id}`}>
                    {s.rounds_today}/{s.target ?? target}
                  </span>
                  {s.incomplete_today > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] shrink-0" title="Trackings incompletos hoy (no cuentan para la meta)" data-testid={`compliance-incomplete-${s.id}`}>
                      +{s.incomplete_today} inc.
                    </span>
                  )}
                  <ChevronDown size={14} className={`text-[#a8a397] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}/>
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#f0ede5] space-y-2" data-testid={`compliance-detail-${s.id}`}>
                    <p className="text-[12px] text-[#3a3a3a] flex items-center gap-1.5">
                      <Clock size={12} className="text-[#8a8579]"/>
                      Último tracking: <strong>{fmtDT(s.last_tracking_at) || 'nunca'}</strong>
                      {s.last_tracking_at && s.last_incomplete && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">incompleto</span>
                      )}
                    </p>
                    {s.times_today.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {s.times_today.map((t, i) => (
                          <span key={i} className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${t.complete ? 'bg-[#f3f0e9] text-[#3a3a3a]' : 'bg-[#fef3c7] text-[#92400e]'}`} title={t.complete ? 'Tracking completado' : 'Tracking incompleto (no cuenta)'}>
                            {t.start ? `${fmtT(t.start)} – ${fmtT(t.at)}` : fmtT(t.at)}{!t.complete && ' · inc.'}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11.5px] text-[#8a8579]">Sin trackings hoy.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10.5px] text-[#8a8579] mt-4">🟢 meta cumplida · 🟡 en progreso · 🔴 sin tracking hoy. La meta de cada estación = sus horarios de tracking configurados para el día. Cuentan solo rondas completadas (todos los markers).</p>
    </div>
  );
};

export default TrackingCompliance;
